import { useState, useEffect, useRef } from "react"
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom"
import {
  ArrowLeft, Plus, Trash2, Share2, Copy, Save,
  Camera, Wrench, Pencil
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog"
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog"
import { supabase } from "@/integrations/supabase/client"
import { useAuth } from "@/contexts/AuthContext"
import { toast } from "sonner"
import { uploadFileToBucket, createSignedFileUrl, PHOTO_BUCKET } from "@/lib/vehicleStorage"
import type { TablesInsert } from "@/integrations/supabase/types"
import { normalizarTelefoneWhatsApp } from "@/lib/oficinaAtendimento"
import { AssistenteVozContextual } from "@/components/ai/AssistenteVozContextual"
import { preencherDiagnostico, type ClienteInfo } from "@/lib/aiFillFormService"

type Urgencia = "ok" | "atencao" | "urgente"

const URGENCIA_CONFIG: Record<Urgencia, { label: string; className: string; activeClass: string }> = {
  ok:      { label: "OK",      className: "border-green-200 bg-green-50 text-green-700",   activeClass: "bg-green-600 text-white border-green-600" },
  atencao: { label: "Atenção", className: "border-amber-200 bg-amber-50 text-amber-700",   activeClass: "bg-amber-500 text-white border-amber-500" },
  urgente: { label: "Urgente", className: "border-red-200 bg-red-50 text-red-700",         activeClass: "bg-red-600 text-white border-red-600" },
}

interface ItemLocal {
  id: string
  titulo: string
  observacao: string
  urgencia: Urgencia
  foto_url: string | null      // storage path (saved)
  foto_preview: string | null  // displayable URL (signed or blob)
  foto_file: File | null       // file pending upload
  ordem: number
}

interface Veiculo {
  placa: string; marca: string; modelo: string; ano: number
}

interface ClienteEntrada {
  nome: string | null
  telefone: string | null
}

const ITEM_VAZIO: Omit<ItemLocal, "id" | "ordem"> = {
  titulo: "", observacao: "", urgencia: "atencao",
  foto_url: null, foto_preview: null, foto_file: null,
}

export default function Diagnostico() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { loja } = useAuth()

  const veiculoIdParam   = searchParams.get("veiculo")
  const avaliacaoIdParam = searchParams.get("avaliacao")

  const [diagnosticoId, setDiagnosticoId] = useState<string | null>(id ?? null)
  const [veiculo, setVeiculo]   = useState<Veiculo | null>(null)
  const [veiculoId, setVeiculoId] = useState<string | null>(null)
  const [avaliacaoId, setAvaliacaoId] = useState<string | null>(avaliacaoIdParam)
  const [clienteEntrada, setClienteEntrada] = useState<ClienteEntrada | null>(null)
  const [titulo, setTitulo]     = useState("")
  const [observacoes, setObs]   = useState("")
  const [itens, setItens]       = useState<ItemLocal[]>([])
  const [shareToken, setShareToken] = useState<string | null>(null)
  const [loading, setLoading]   = useState(true)
  const [salvando, setSalvando] = useState(false)
  const [compartilhando, setCompartilhando] = useState(false)
  const [iaProcessando, setIaProcessando] = useState(false)

  // Dialog de item
  const [dialogAberto, setDialogAberto] = useState(false)
  const [itemEditando, setItemEditando] = useState<ItemLocal | null>(null)
  const [itemForm, setItemForm] = useState<Omit<ItemLocal, "id" | "ordem">>(ITEM_VAZIO)
  const [salvandoItem, setSalvandoItem] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    void init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const init = async () => {
    if (!loja?.id) return

    if (id) {
      await carregarDiagnostico(id)
    } else if (veiculoIdParam) {
      await criarDiagnostico(veiculoIdParam, avaliacaoIdParam)
    } else {
      navigate("/veiculos", { replace: true })
      return
    }
    setLoading(false)
  }

  const resolverAvaliacaoAtual = async (vId: string, aId: string | null) => {
    if (aId) return aId

    const { data } = await supabase
      .from("avaliacoes")
      .select("id")
      .eq("veiculo_id", vId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle()

    return data?.id ?? null
  }

  const carregarClienteEntrada = async (aId: string | null) => {
    if (!aId) {
      setClienteEntrada(null)
      return
    }

    const { data } = await supabase
      .from("avaliacoes")
      .select("cliente_nome, cliente_telefone")
      .eq("id", aId)
      .maybeSingle()

    setClienteEntrada(data
      ? { nome: data.cliente_nome ?? null, telefone: data.cliente_telefone ?? null }
      : null
    )
  }

  const criarDiagnostico = async (vId: string, aId: string | null) => {
    const avaliacaoAtualId = await resolverAvaliacaoAtual(vId, aId)
    const payload: TablesInsert<"diagnosticos"> = {
      loja_id: loja!.id,
      veiculo_id: vId,
      avaliacao_id: avaliacaoAtualId,
    }
    const { data, error } = await supabase
      .from("diagnosticos")
      .insert(payload)
      .select("id, veiculo_id")
      .single()

    if (error || !data) { toast.error("Erro ao criar diagnóstico"); return }

    setDiagnosticoId(data.id)
    setVeiculoId(data.veiculo_id)
    setAvaliacaoId(avaliacaoAtualId)
    navigate(`/diagnostico/${data.id}`, { replace: true })
    await carregarVeiculo(data.veiculo_id)
    await carregarClienteEntrada(avaliacaoAtualId)
  }

  const carregarDiagnostico = async (dId: string) => {
    const { data, error } = await supabase
      .from("diagnosticos")
      .select("*, diagnosticos_itens(*)")
      .eq("id", dId)
      .single()

    if (error || !data) { toast.error("Diagnóstico não encontrado"); return }

    setTitulo(data.titulo ?? "")
    setObs(data.observacoes ?? "")
    setShareToken(data.share_token ?? null)
    setVeiculoId(data.veiculo_id)
    setAvaliacaoId(data.avaliacao_id ?? null)

    const itensRaw = (data.diagnosticos_itens as Array<{
      id: string; titulo: string; observacao: string | null;
      urgencia: string; foto_url: string | null; ordem: number
    }>) ?? []

    const itensCarregados: ItemLocal[] = await Promise.all(
      itensRaw
        .sort((a, b) => a.ordem - b.ordem)
        .map(async (item, idx) => ({
          id: item.id,
          titulo: item.titulo,
          observacao: item.observacao ?? "",
          urgencia: (item.urgencia as Urgencia) ?? "atencao",
          foto_url: item.foto_url,
          foto_preview: item.foto_url
            ? (await createSignedFileUrl(PHOTO_BUCKET, item.foto_url)) ?? null
            : null,
          foto_file: null,
          ordem: item.ordem ?? idx,
        }))
    )
    setItens(itensCarregados)
    await carregarVeiculo(data.veiculo_id)
    await carregarClienteEntrada(data.avaliacao_id ?? null)
  }

  const carregarVeiculo = async (vId: string) => {
    const { data } = await supabase
      .from("veiculos")
      .select("placa, marca, modelo, ano")
      .eq("id", vId)
      .single()
    if (data) setVeiculo(data)
  }

  // ── Salvar ──────────────────────────────────────────────
  const handleSalvar = async () => {
    if (!diagnosticoId) return
    setSalvando(true)
    try {
      const { error } = await supabase
        .from("diagnosticos")
        .update({ titulo: titulo || null, observacoes: observacoes || null, updated_at: new Date().toISOString() })
        .eq("id", diagnosticoId)
      if (error) throw error
      toast.success("Diagnóstico salvo")
    } catch {
      toast.error("Erro ao salvar diagnóstico")
    } finally {
      setSalvando(false)
    }
  }

  // ── Compartilhar ─────────────────────────────────────────
  const handleCompartilhar = async () => {
    if (!diagnosticoId || !veiculo) return
    setCompartilhando(true)
    try {
      const token = shareToken ?? crypto.randomUUID()
      if (!shareToken) {
        const { error } = await supabase
          .from("diagnosticos")
          .update({ share_token: token, status: "compartilhado" })
          .eq("id", diagnosticoId)
        if (error) throw error
        setShareToken(token)
      }
      const url = `${window.location.origin}/d/${token}`
      const texto = `Olá${clienteEntrada?.nome ? `, ${clienteEntrada.nome}` : ""}! Finalizamos o diagnóstico técnico do veículo *${veiculo.placa}* (${veiculo.marca} ${veiculo.modelo}). Você pode visualizar os pontos encontrados e registrar sua ciência/autorização aqui:\n${url}`
      const telefone = normalizarTelefoneWhatsApp(clienteEntrada?.telefone)
      window.open(`https://wa.me/${telefone}?text=${encodeURIComponent(texto)}`, "_blank")
    } catch {
      toast.error("Erro ao gerar link")
    } finally {
      setCompartilhando(false)
    }
  }

  // ── Item: abrir dialog ───────────────────────────────────
  const abrirNovoItem = () => {
    setItemEditando(null)
    setItemForm({ ...ITEM_VAZIO })
    setDialogAberto(true)
  }

  const abrirEditarItem = (item: ItemLocal) => {
    setItemEditando(item)
    setItemForm({
      titulo: item.titulo, observacao: item.observacao,
      urgencia: item.urgencia, foto_url: item.foto_url,
      foto_preview: item.foto_preview, foto_file: item.foto_file,
    })
    setDialogAberto(true)
  }

  const atualizarClienteEntrada = async (cliente: ClienteInfo) => {
    if (!avaliacaoId) return
    if (!cliente.nome && !cliente.telefone) return
    const update: Record<string, string> = {}
    if (cliente.nome) update.cliente_nome = cliente.nome
    if (cliente.telefone) update.cliente_telefone = cliente.telefone
    const { error } = await supabase.from("avaliacoes").update(update).eq("id", avaliacaoId)
    if (!error) {
      setClienteEntrada(prev => ({
        nome: cliente.nome ?? prev?.nome ?? null,
        telefone: cliente.telefone ?? prev?.telefone ?? null,
      }))
    }
  }

  const handleVozDiagnostico = async (transcricao: string, contexto: string | null): Promise<string | null> => {
    if (!diagnosticoId || !loja?.id) {
      toast.error("Diagnóstico ainda não salvo. Aguarde um momento.")
      return null
    }

    setIaProcessando(true)
    try {
      const { itens: novosItens, cliente, mensagem } = await preencherDiagnostico(transcricao, contexto)

      await atualizarClienteEntrada(cliente)

      if (!novosItens.length) {
        return mensagem || null
      }

      const ordemAtual = itens.length

      const itensParaInserir = novosItens.map((item, idx) => ({
        diagnostico_id: diagnosticoId,
        titulo: item.titulo.trim(),
        observacao: item.observacao || null,
        urgencia: item.urgencia,
        ordem: ordemAtual + idx,
      }))

      const { data: inseridos, error } = await supabase
        .from("diagnosticos_itens")
        .insert(itensParaInserir)
        .select("id, titulo, observacao, urgencia, ordem")

      if (error) throw error

      const novosLocais = (inseridos ?? []).map(i => ({
        id: i.id as string,
        titulo: i.titulo as string,
        observacao: (i.observacao as string) ?? "",
        urgencia: i.urgencia as "ok" | "atencao" | "urgente",
        foto_url: null,
        foto_preview: null,
        foto_file: null,
        ordem: i.ordem as number,
      }))

      setItens(prev => [...prev, ...novosLocais])

      return mensagem || null
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro ao processar voz"
      toast.error(message)
      return null
    } finally {
      setIaProcessando(false)
    }
  }

  // ── Item: foto local ─────────────────────────────────────
  const handleFotoSelecionada = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const preview = URL.createObjectURL(file)
    setItemForm(prev => ({ ...prev, foto_file: file, foto_preview: preview }))
  }

  // ── Item: salvar (DB) ─────────────────────────────────────
  const handleSalvarItem = async () => {
    if (!diagnosticoId || !itemForm.titulo.trim()) {
      toast.error("Informe o título do item")
      return
    }
    setSalvandoItem(true)

    let fotoPath = itemForm.foto_url

    // Upload de foto se há arquivo novo
    if (itemForm.foto_file) {
      try {
        const { path } = await uploadFileToBucket(
          PHOTO_BUCKET,
          itemForm.foto_file,
          `diagnostico/${loja!.id}/${diagnosticoId}`
        )
        fotoPath = path
      } catch {
        toast.error("Erro ao enviar foto")
        setSalvandoItem(false)
        return
      }
    }

    try {
      if (itemEditando) {
        // Atualizar item existente
        const { error } = await supabase
          .from("diagnosticos_itens")
          .update({
            titulo: itemForm.titulo.trim(),
            observacao: itemForm.observacao || null,
            urgencia: itemForm.urgencia,
            foto_url: fotoPath,
          })
          .eq("id", itemEditando.id)

        if (error) throw error

        setItens(prev => prev.map(i =>
          i.id === itemEditando.id
            ? { ...i, titulo: itemForm.titulo.trim(), observacao: itemForm.observacao,
                urgencia: itemForm.urgencia, foto_url: fotoPath,
                foto_preview: itemForm.foto_preview, foto_file: null }
            : i
        ))
      } else {
        // Criar novo item
        const ordem = itens.length
        const { data, error } = await supabase
          .from("diagnosticos_itens")
          .insert({
            diagnostico_id: diagnosticoId,
            titulo: itemForm.titulo.trim(),
            observacao: itemForm.observacao || null,
            urgencia: itemForm.urgencia,
            foto_url: fotoPath,
            ordem,
          })
          .select("id")
          .single()

        if (error || !data) throw error ?? new Error("no data")

        setItens(prev => [...prev, {
          id: data.id, titulo: itemForm.titulo.trim(),
          observacao: itemForm.observacao, urgencia: itemForm.urgencia,
          foto_url: fotoPath, foto_preview: itemForm.foto_preview,
          foto_file: null, ordem,
        }])
      }
      setDialogAberto(false)
    } catch {
      toast.error("Erro ao salvar item")
    } finally {
      setSalvandoItem(false)
    }
  }

  // ── Item: deletar ─────────────────────────────────────────
  const handleDeletarItem = async (itemId: string) => {
    const { error } = await supabase
      .from("diagnosticos_itens")
      .delete()
      .eq("id", itemId)
    if (error) { toast.error("Erro ao remover item"); return }
    setItens(prev => prev.filter(i => i.id !== itemId))
  }

  // ── Diagnóstico: deletar ──────────────────────────────────
  const handleDeletarDiagnostico = async () => {
    if (!id) return
    try {
      const { data: itensComFoto } = await supabase
        .from("diagnosticos_itens")
        .select("foto_url")
        .eq("diagnostico_id", id)
      const paths = (itensComFoto ?? [])
        .map((i) => i.foto_url)
        .filter((p): p is string => Boolean(p))
      if (paths.length > 0) {
        await supabase.storage.from(PHOTO_BUCKET).remove(paths)
      }
      const { error } = await supabase
        .from("diagnosticos")
        .delete()
        .eq("id", id)
      if (error) { toast.error("Erro ao excluir diagnóstico"); return }
      toast.success("Diagnóstico excluído")
      navigate(veiculoId ? `/veiculo/${veiculoId}` : "/veiculos")
    } catch {
      toast.error("Erro inesperado ao excluir diagnóstico")
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }

  const voltarHref = veiculoId ? `/veiculo/${veiculoId}` : "/veiculos"

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to={voltarHref}>
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Diagnóstico Técnico</h1>
          {veiculo && (
            <p className="text-muted-foreground text-sm">
              {veiculo.placa} — {veiculo.marca} {veiculo.modelo} {veiculo.ano}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
        {/* Coluna esquerda: info + ações */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Wrench className="h-4 w-4" />
                Diagnóstico
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="titulo">Título (opcional)</Label>
                <Input
                  id="titulo"
                  value={titulo}
                  onChange={e => setTitulo(e.target.value)}
                  placeholder="Ex: Revisão 30.000 km"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="obs">Observações gerais</Label>
                <Textarea
                  id="obs"
                  value={observacoes}
                  onChange={e => setObs(e.target.value)}
                  placeholder="Observações gerais sobre o diagnóstico..."
                  className="resize-none"
                  rows={3}
                />
              </div>

              {/* Resumo */}
              <div className="rounded-xl border bg-muted/20 p-3 space-y-2">
                {avaliacaoId ? (
                  <div className="flex justify-between gap-3 text-sm">
                    <span className="text-muted-foreground">Entrada vinculada</span>
                    <span className="font-semibold">Sim</span>
                  </div>
                ) : (
                  <p className="rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
                    Este diagnóstico não está vinculado a uma entrada.
                  </p>
                )}
                {clienteEntrada?.nome || clienteEntrada?.telefone ? (
                  <div className="border-t pt-2 text-sm">
                    <p className="font-medium">{clienteEntrada.nome ?? "Cliente não identificado"}</p>
                    {clienteEntrada.telefone && (
                      <p className="text-xs text-muted-foreground">{clienteEntrada.telefone}</p>
                    )}
                  </div>
                ) : null}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total de itens</span>
                  <span className="font-semibold">{itens.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Urgentes</span>
                  <span className="font-semibold text-red-600">
                    {itens.filter(i => i.urgencia === "urgente").length}
                  </span>
                </div>
              </div>

              {/* Ações */}
              <div className="space-y-2 pt-1">
                <Button
                  className="w-full bg-green-600 text-white hover:bg-green-700"
                  onClick={handleCompartilhar}
                  disabled={compartilhando || itens.length === 0}
                >
                  {compartilhando
                    ? <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-current" />
                    : <Share2 className="mr-2 h-4 w-4" />
                  }
                  {shareToken ? "Compartilhar novamente" : "Compartilhar via WhatsApp"}
                </Button>
                <Button variant="outline" className="w-full" onClick={handleSalvar} disabled={salvando}>
                  {salvando
                    ? <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-current" />
                    : <Save className="mr-2 h-4 w-4" />
                  }
                  Salvar
                </Button>
                {shareToken && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs text-muted-foreground"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/d/${shareToken}`)
                        .then(() => toast.success("Link copiado"))
                        .catch(() => toast.error("Erro ao copiar"))
                    }}
                  >
                    <Copy className="mr-1.5 h-3.5 w-3.5" />
                    Copiar link
                  </Button>
                )}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      className="w-full text-destructive hover:bg-transparent hover:text-destructive/90"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Excluir diagnóstico
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir este diagnóstico? Todos os itens e fotos
                        associados serão removidos permanentemente.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => void handleDeletarDiagnostico()}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        Confirmar Exclusão
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Coluna direita: itens */}
        <div className="space-y-4">
          {itens.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed bg-muted/20 py-16 text-center gap-3">
              <Wrench className="h-10 w-10 text-muted-foreground opacity-30" />
              <p className="text-sm text-muted-foreground">Nenhum item adicionado ainda.</p>
              <Button onClick={abrirNovoItem} variant="outline">
                <Plus className="mr-2 h-4 w-4" />
                Adicionar primeiro item
              </Button>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {itens.map((item) => {
                  const cfg = URGENCIA_CONFIG[item.urgencia]
                  return (
                    <div key={item.id} className="flex gap-3 rounded-xl border bg-white shadow-sm p-3 items-center">
                      {item.foto_preview ? (
                        <img
                          src={item.foto_preview}
                          alt={item.titulo}
                          className="h-14 w-14 shrink-0 rounded-lg object-cover border"
                        />
                      ) : (
                        <div className="h-14 w-14 shrink-0 rounded-lg border bg-muted flex items-center justify-center">
                          <Camera className="h-4 w-4 text-muted-foreground opacity-40" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium leading-snug truncate">{item.titulo}</p>
                        {item.observacao && (
                          <p className="text-xs text-muted-foreground truncate">{item.observacao}</p>
                        )}
                      </div>
                      <Badge variant="outline" className={`text-[10px] shrink-0 ${cfg.className}`}>
                        {cfg.label}
                      </Badge>
                      <div className="flex gap-1 shrink-0">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => abrirEditarItem(item)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDeletarItem(item.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
              <Button variant="outline" className="w-full" onClick={abrirNovoItem}>
                <Plus className="mr-2 h-4 w-4" />
                Adicionar item
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Dialog de item */}
      <Dialog open={dialogAberto} onOpenChange={setDialogAberto}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{itemEditando ? "Editar item" : "Novo item"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Foto */}
            <div
              className="relative w-full rounded-xl border-2 border-dashed bg-muted/30 flex items-center justify-center cursor-pointer overflow-hidden"
              style={{ height: 160 }}
              onClick={() => fileInputRef.current?.click()}
            >
              {itemForm.foto_preview ? (
                <img
                  src={itemForm.foto_preview}
                  alt="preview"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <Camera className="h-8 w-8 opacity-40" />
                  <p className="text-xs">Toque para adicionar foto</p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFotoSelecionada}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="item-titulo">Título *</Label>
              <Input
                id="item-titulo"
                value={itemForm.titulo}
                onChange={e => setItemForm(p => ({ ...p, titulo: e.target.value }))}
                placeholder="Ex: Pastilhas de freio dianteiras"
                autoFocus
              />
            </div>

            {/* Urgência */}
            <div className="space-y-1.5">
              <Label>Urgência</Label>
              <div className="grid grid-cols-3 gap-2">
                {(["ok", "atencao", "urgente"] as Urgencia[]).map((u) => {
                  const cfg = URGENCIA_CONFIG[u]
                  const ativo = itemForm.urgencia === u
                  return (
                    <button
                      key={u}
                      type="button"
                      onClick={() => setItemForm(p => ({ ...p, urgencia: u }))}
                      className={`rounded-lg border px-2 py-2 text-xs font-medium transition-colors ${
                        ativo ? cfg.activeClass : cfg.className
                      }`}
                    >
                      {cfg.label}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="item-obs">Observação (opcional)</Label>
              <Textarea
                id="item-obs"
                value={itemForm.observacao}
                onChange={e => setItemForm(p => ({ ...p, observacao: e.target.value }))}
                placeholder="Detalhes adicionais..."
                rows={2}
                className="resize-none"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogAberto(false)} disabled={salvandoItem}>Cancelar</Button>
            <Button onClick={handleSalvarItem} disabled={!itemForm.titulo.trim() || salvandoItem}>
              {salvandoItem
                ? <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-current" />
                : null
              }
              Salvar item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AssistenteVozContextual
        onTranscricao={handleVozDiagnostico}
        processando={iaProcessando}
      />
    </div>
  )
}
